
import { h } from 'preact'
import { memo, useRef, useState } from 'preact/compat'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  useMyOrganizations,
  useMyPublicProfile,
  usePublicProfiles,
} from 'lib/membershipAppStateHooks'

import { useChangeChatChannelAttributes } from 'lib/chatHooks'
import { useAppState } from 'lib/appState'
import Button from 'components/Button'
import Icon from 'components/Icon'
import ChatChannelAvatar from 'components/ChatChannelAvatar'
import EndUserAvatar from 'components/EndUserAvatar'
import './index.sass'

const MEDIA_ATTACHMENT_PREFIX = 'jlinc:dm:attachment:'

const ChatNotificationsList = memo(({ synopsis = [], onMemberClick }) => {
  const { myPublicProfile } = useMyPublicProfile('ChatNotificationsList')
  const { myOrganizations } = useMyOrganizations('ChatNotificationsList')
  const { changeChatChannelAttributes } = useChangeChatChannelAttributes('ChatNotificationsList')
  const { takeAction } = useAppState({}, 'ChatNotificationsList')
  const containerRef = useRef(null)

  const [markedAsRead, setMarkedAsRead] = useState(new Set())

  const unreadChats = synopsis.filter(chat =>
    (chat.type === 'general' && (chat.unread > 0 || chat.is_unread)) ||
    (!markedAsRead.has(chat.uid) &&
     (chat.unread > 0 || chat.is_unread) &&
     !chat.is_notifications_muted)
  )


  const publicProfileDids = new Set()
  unreadChats.forEach(chatChannel =>
    (chatChannel.memberUserDids || []).forEach(did => publicProfileDids.add(did))
  )
  const { publicProfiles } = usePublicProfiles(publicProfileDids, 'ChatNotificationsList')

  const senderPublicProfileDids = new Set()
  unreadChats.map(chatChannel => {
    if(chatChannel.senderUserDid)
      senderPublicProfileDids.add(chatChannel.senderUserDid)
  })
  const { publicProfiles: senderProfiles } = usePublicProfiles(senderPublicProfileDids, 'ChatNotificationsList')

  const markIndividualMessagesAsRead = async (chatUid) => {
    try {
      await takeAction('chat.markAllChatMessagesAsRead', chatUid)
      await changeChatChannelAttributes(chatUid, { is_unread: false })
      const synopsisIndex = synopsis.findIndex(s => s.uid === chatUid)
      if (synopsisIndex >= 0) {
        synopsis[synopsisIndex].unread = 0
        synopsis[synopsisIndex].is_unread = false
      }
      return true
    } catch (error) {
      console.error(`Error marking messages as read for chat ${chatUid}:`, error)
      return false
    }
  }

  const handleMarkAllAsRead = async () => {
    const newMarkedAsRead = new Set(markedAsRead)
    const markPromises = unreadChats.map(async (chat) => {
      newMarkedAsRead.add(chat.uid)
      await markIndividualMessagesAsRead(chat.uid)
    })
    await Promise.all(markPromises)
    setMarkedAsRead(newMarkedAsRead)
  }

  const handleNotificationClick = async (chatChannel) => {
    setMarkedAsRead(prev => {
      const newSet = new Set(prev)
      newSet.add(chatChannel.uid)
      return newSet
    })
    await markIndividualMessagesAsRead(chatChannel.uid)
    if (onMemberClick) onMemberClick()
    window.location.href = `/chat/${chatChannel.uid}`
  }

  const stripHTML = (html) => {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent.trim()
  }

  const processPreview = (preview, senderProfile) => {
    if (!preview) return ''

    if (preview.startsWith(MEDIA_ATTACHMENT_PREFIX)) {
      const match = preview.match(/"type":"([^"]+)"/)
      return match ? `sent ${RegExp.$1}` : 'sent media attachment'
    }

    const cleanText = stripHTML(preview)
    const truncated = cleanText.length > 27 ? cleanText.substring(0, 27) + '...' : cleanText

    return senderProfile ? `${senderProfile.displayName || senderProfile.username}: ${truncated}` : truncated
  }

  return (
    <div className="ChatNotificationsList">
      <div className="ChatNotificationsList-header">
        <div className="ChatNotificationsList-title">Chat Notification</div>
        <Button
          className="see-all-chats"
          type="primary"
          size="sm"
          href="/chat"
        >
          See All Chats
        </Button>
      </div>

      {unreadChats.length > 0 && (
        <div className="ChatNotificationsList-action" onClick={handleMarkAllAsRead}>
          <Button
            className="mark-all-read"
            size="sm"
            plain
          >
            <Icon type="read" size="sm" />
            <span>Mark all Read</span>
          </Button>
        </div>
      )}

      <div
        className="ChatNotificationsList-items"
        ref={containerRef}
        style={{ maxHeight: 'calc(4.5 * 64px)' }}
      >
        {unreadChats.length > 0 ? (
          unreadChats
            .sort(sortChats)
            .map(chatChannel => {
              const organization = myOrganizations.find(o =>
                o.apikey === chatChannel.organizationApikey
              ) || {apikey: chatChannel.organizationApikey}


              const isGeneral = chatChannel.type === 'general'
              const isGroupChat = chatChannel.isGroupChat ||
                (chatChannel.memberUserDids && chatChannel.memberUserDids.length > 2)
              // const isDirectMessage = !isGeneral && !isGroupChat


              const otherPublicProfiles = (chatChannel.memberUserDids || [])
                .filter(did => did !== myPublicProfile?.did)
                .map(did => publicProfiles.find(p => p.did === did))
                .filter(Boolean)

              const senderProfile = senderProfiles?.find(p => p.did === chatChannel.senderUserDid)

              let name, avatar, message

              if (isGeneral) {
                avatar = (
                  <div className="chat-hashtag">
                    <Icon type="hashtag" size="sm" />
                  </div>
                )

                name = `${organization.name || organization.apikey || ''} - General`
              } else if (isGroupChat) {

                if (chatChannel.imageUrl && !chatChannel.imageUrl.startsWith('#')) {
                  avatar = (
                    <ChatChannelAvatar
                      size="sm"
                      value={
                        <img
                          src={chatChannel.imageUrl}
                          alt="Chat Image"
                          className="avatarImg"
                          style={{padding: 0}}
                        />
                      }
                    />
                  )
                } else {
                  avatar = (
                    <div className="chat-hashtag">
                      <Icon type="hashtagGroup" size="sm" />
                    </div>
                  )
                }

                name = chatChannel.name || 'Group Chat'
              } else {
                const otherMemberProfile = otherPublicProfiles[0]

                avatar = otherMemberProfile ? (
                  <EndUserAvatar publicProfile={otherMemberProfile} size="sm" />
                ) : (
                  <div className="chat-avatar-placeholder">
                    <Icon type="user" size="sm" />
                  </div>
                )

                name = otherMemberProfile?.displayName || otherMemberProfile?.username || 'Chat'
              }

              message = processPreview(chatChannel.preview, senderProfile)
              const date = chatChannel.latestAt || chatChannel.createdAt
              let formattedDate
              const now = moment()
              const messageDate = moment(date)

              if (now.diff(messageDate, 'days') === 0) {
                formattedDate = messageDate.format('h:mm A')
              } else if (now.diff(messageDate, 'days') < 7) {
                formattedDate = messageDate.format('dddd')
              } else if (now.diff(messageDate, 'years') === 0) {
                formattedDate = messageDate.format('MMM D')
              } else {
                formattedDate = messageDate.format('MMM D, YYYY')
              }

              return (
                <div
                  key={chatChannel.uid}
                  className="notification-item"
                  onClick={() => handleNotificationClick(chatChannel)}
                  data-chat-uid={chatChannel.uid}
                  data-chat-type={isGeneral ? 'general' : (isGroupChat ? 'group' : 'direct')}
                >
                  {(chatChannel.unread > 0 || chatChannel.is_unread) &&
                    <div className="notification-indicator" />
                  }
                  <div className="notification-avatar">
                    {avatar}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {name?.length > 30 ? name.substring(0, 30) + '...' : name}
                    </div>
                    <div className="notification-message">
                      {message}
                    </div>
                  </div>
                  <div className="notification-time">
                    {formattedDate}
                  </div>
                </div>
              )
            })
        ) : (
          <div className="empty-state">
            <Icon type="messages" size="lg" />
            <p>No unread messages</p>
          </div>
        )}
      </div>
    </div>
  )
})

ChatNotificationsList.propTypes = {
  synopsis: PropTypes.array,
  onMemberClick: PropTypes.func,
}

export default ChatNotificationsList

function sortChats(a, b) {
  if (a.unread > 0 && b.unread === 0) return -1
  if (a.unread === 0 && b.unread > 0) return 1
  return moment(b.latestAt || b.createdAt).valueOf() - moment(a.latestAt || a.createdAt).valueOf()
}
